import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import AccountLayout from '@/components/feature/AccountLayout';
import { useAuth } from '@/hooks/useAuth';
import { fetchMyJobPostings, closeJobPosting, submitJobPostingAudit } from '@/service/api/job';
import { Toast, ConfirmModal } from '@/components/common';
import MoreActionMenu from '@/components/common/MoreActionMenu';
import JobApplicationsModal from './modules/JobApplicationsModal';

function formatDate(dateStr?: string) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function getEmploymentTypeLabel(type?: string) {
  switch (type) {
    case 'FULL_TIME':
      return '全职';
    case 'PART_TIME':
      return '兼职';
    case 'INTERNSHIP':
      return '实习';
    case 'PROJECT':
      return '项目/外包';
    default:
      return type || '全职';
  }
}

function formatSalary(post: Api.Job.JobResponse) {
  if (post.salary_type === 'NEGOTIABLE') {
    return '薪资面议';
  }
  const periodMap: Record<string, string> = {
    MONTHLY: '月',
    YEARLY: '年',
    DAILY: '日',
    HOURLY: '小时',
    PROJECT: '次',
  };
  const periodText = periodMap[post.salary_period || 'MONTHLY'] || '月';
  const currency = post.salary_currency || 'CNY';

  if (post.salary_type === 'FIXED') {
    return post.salary_min ? `${post.salary_min} ${currency} / ${periodText}` : '薪资面议';
  }

  if (post.salary_min && post.salary_max) {
    return `${post.salary_min} - ${post.salary_max} ${currency} / ${periodText}`;
  }
  if (post.salary_min) {
    return `≥ ${post.salary_min} ${currency} / ${periodText}`;
  }
  return '薪资面议';
}

function getStatusInfo(status?: string) {
  const s = (status || '').toUpperCase();
  if (s === 'PENDING_REVIEW' || s === 'PENDING' || s === 'IN_AUDIT') {
    return { label: '审核中', color: 'bg-amber-50 text-amber-700 border-amber-200/60', type: 'in_audit' };
  }
  if (s === 'REJECTED' || s === 'AUDIT_REJECTED') {
    return { label: '已驳回', color: 'bg-red-50 text-red-600 border-red-200/60', type: 'rejected' };
  }
  if (s === 'RECRUITING' || s === 'PUBLISHED' || s === 'APPROVED') {
    return { label: '已发布', color: 'bg-emerald-50 text-emerald-700 border-emerald-200/60', type: 'published' };
  }
  if (s === 'DRAFT') {
    return { label: '草稿', color: 'bg-slate-100 text-slate-600 border-slate-200/60', type: 'draft' };
  }
  if (s === 'CLOSED') {
    return { label: '已关闭', color: 'bg-slate-100 text-slate-500 border-slate-200/60', type: 'closed' };
  }
  return { label: status || '未知', color: 'bg-slate-100 text-slate-500 border-slate-200/60', type: 'other' };
}

export default function MyJobPostingsPage() {
  const [activeStatus, setActiveStatus] = useState<string>('all');
  const [postings, setPostings] = useState<Api.Job.JobResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'info' | 'warning' } | null>(null);
  const [confirmCloseId, setConfirmCloseId] = useState<string | null>(null);
  const [isClosing, setIsClosing] = useState(false);

  // 查看申请列表选中的岗位
  const [selectedJobForApps, setSelectedJobForApps] = useState<Api.Job.JobResponse | null>(null);

  const { user, isEnterprise } = useAuth();
  const userId = user?.id;
  const isEnterpriseVerified = isEnterprise && user?.verified;

  const showToast = (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setToast({ message, type });
  };

  const loadJobs = useCallback(async () => {
    setIsLoading(true);
    setLoadError('');
    try {
      const res = await fetchMyJobPostings();
      setPostings(res?.items || []);
    } catch (err: any) {
      setLoadError('招聘岗位列表加载失败，请稍后重试');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isEnterpriseVerified) {
      loadJobs();
    } else {
      setIsLoading(false);
    }
  }, [userId, isEnterpriseVerified, loadJobs]);

  const handleConfirmClose = async () => {
    if (!confirmCloseId) return;
    setIsClosing(true);
    try {
      await closeJobPosting(confirmCloseId);
      showToast('已关闭招聘岗位', 'success');
      setConfirmCloseId(null);
      loadJobs();
    } catch (error: any) {
      console.error('关闭招聘岗位失败:', error);
      showToast(error?.message || '关闭招聘岗位失败', 'error');
    } finally {
      setIsClosing(false);
    }
  };

  const handleSubmitAudit = async (id?: string) => {
    if (!id) return;
    try {
      await submitJobPostingAudit(id);
      showToast('已成功提交审核', 'success');
      loadJobs();
    } catch (error: any) {
      console.error('提交审核失败:', error);
      showToast(error?.message || '提交审核失败', 'error');
    }
  };

  const filteredPostings = postings.filter((post) => {
    const info = getStatusInfo(post.status);
    if (activeStatus === 'all') return true;
    if (activeStatus === 'in_audit') return info.type === 'in_audit';
    if (activeStatus === 'audit_rejected') return info.type === 'rejected';
    if (activeStatus === 'published') return info.type === 'published';
    if (activeStatus === 'draft') return info.type === 'draft';
    if (activeStatus === 'closed') return info.type === 'closed';
    return true;
  });

  return (
    <AccountLayout
      activePath="/my/job-postings"
      breadcrumbs={[{ label: '账号中心', href: '/profile' }, { label: '招聘管理' }]}
    >
      {/* 头部标题与发布操作 */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
        <div>
          <h1 className="font-bold text-xl text-slate-900">招聘岗位管理</h1>
          <p className="text-xs text-slate-500 mt-1">发布与管理您企业的招聘岗位，吸引合适的全流程 AIGC 人才</p>
        </div>
        {isEnterpriseVerified && (
          <Link
            to="/post-job"
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-xs font-semibold transition-all shadow-md shadow-primary-500/20 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line text-sm"></i>
            发布招聘岗位
          </Link>
        )}
      </div>

      {!isEnterprise ? (
        /* 非企业账户提示 */
        <div className="bg-white rounded-2xl p-10 text-center border border-slate-100 shadow-sm">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-amber-50 flex items-center justify-center">
            <i className="ri-building-4-line text-3xl text-amber-500"></i>
          </div>
          <h3 className="text-base font-semibold text-slate-900 mb-2">招聘管理仅面向企业账号开放</h3>
          <p className="text-sm text-slate-500 max-w-md mx-auto mb-6 leading-relaxed">
            招聘岗位发布与管理属于企业专属功能。如需在线发布招聘岗位招募人才，请先完成企业认证转为企业账号。
          </p>
          <Link
            to="/certification"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-medium text-xs transition-all shadow-md shadow-primary-500/20"
          >
            前往企业认证
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      ) : !isEnterpriseVerified ? (
        /* 未认证企业提示 */
        <div className="bg-white rounded-2xl p-10 text-center border border-slate-100 shadow-sm">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-amber-50 flex items-center justify-center">
            <i className="ri-shield-user-line text-3xl text-amber-500"></i>
          </div>
          <h3 className="text-base font-semibold text-slate-900 mb-2">需要完成企业认证</h3>
          <p className="text-sm text-slate-500 max-w-md mx-auto mb-6">
            发布与管理招聘岗位需先完成企业认证。完成企业认证后即可在线发布岗位招纳人才。
          </p>
          <Link
            to="/certification"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-medium text-xs transition-all shadow-md shadow-primary-500/20"
          >
            前往企业认证
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      ) : (
        <>
          {/* 状态筛选按钮 */}
          <div className="flex flex-wrap items-center gap-2 mb-6">
            {[
              { key: 'all', label: '全部' },
              { key: 'published', label: '已发布' },
              { key: 'in_audit', label: '审核中' },
              { key: 'draft', label: '草稿' },
              { key: 'audit_rejected', label: '已驳回' },
              { key: 'closed', label: '已关闭' },
            ].map((s) => (
              <button
                key={s.key}
                onClick={() => setActiveStatus(s.key)}
                className={`text-xs px-3.5 py-1.5 rounded-full transition-all cursor-pointer whitespace-nowrap font-medium ${
                  activeStatus === s.key
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* 岗位列表 */}
          <div className="space-y-4">
            {isLoading ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-slate-100">
                <div className="w-8 h-8 mx-auto mb-3 rounded-full border-2 border-primary-200 border-t-primary-600 animate-spin"></div>
                <p className="text-xs text-slate-400">正在加载招聘岗位管理记录...</p>
              </div>
            ) : loadError ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-slate-100">
                <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-red-50 flex items-center justify-center">
                  <i className="ri-error-warning-line text-xl text-red-500"></i>
                </div>
                <p className="text-xs text-red-500">{loadError}</p>
              </div>
            ) : filteredPostings.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-slate-100">
                <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-slate-100 flex items-center justify-center">
                  <i className="ri-user-search-line text-2xl text-slate-400"></i>
                </div>
                <p className="text-sm font-medium text-slate-700">暂无匹配的招聘岗位</p>
                <p className="text-xs text-slate-400 mt-1">您可以通过右上角“发布招聘岗位”按钮开启招聘</p>
              </div>
            ) : (
              filteredPostings.map((post) => {
                const statusInfo = getStatusInfo(post.status);
                const empTypeLabel = getEmploymentTypeLabel(post.employment_type);
                const salaryStr = formatSalary(post);

                return (
                  <div
                    key={post.id}
                    className="bg-white rounded-2xl p-5 sm:p-6 border border-slate-100 shadow-sm hover:border-slate-200 transition-all"
                  >
                    {/* Top Row: 就业类型 Badge + 岗位 ID + 状态 Badge */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs px-2.5 py-0.5 rounded-full font-medium bg-primary-50 text-primary-700 border border-primary-200/60">
                          {empTypeLabel}
                        </span>
                        {post.city_name && (
                          <span className="text-xs text-slate-500 flex items-center gap-0.5">
                            <i className="ri-map-pin-line text-slate-400"></i>
                            {post.city_name}
                          </span>
                        )}
                      </div>
                      <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium border ${statusInfo.color}`}>
                        {statusInfo.label}
                      </span>
                    </div>

                    {/* Title & Salary */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                      {statusInfo.type === 'published' ? (
                        <Link
                          to={`/job-postings/${post.id}`}
                          className="text-base font-bold text-slate-900 hover:text-primary-600 transition-colors cursor-pointer line-clamp-1"
                        >
                          {post.title}
                        </Link>
                      ) : (
                        <span className="text-base font-bold text-slate-900 line-clamp-1">
                          {post.title}
                        </span>
                      )}
                      <span className="text-sm font-semibold text-emerald-600 flex-shrink-0">
                        {salaryStr}
                      </span>
                    </div>

                    {/* Meta Info */}
                    <div className="flex flex-wrap items-center gap-4 text-xs text-slate-400 mb-4">
                      {post.published_at && (
                        <span className="flex items-center gap-1">
                          <i className="ri-calendar-line"></i>发布于 {formatDate(post.published_at)}
                        </span>
                      )}
                      {post.deadline_at && (
                        <span className="flex items-center gap-1">
                          <i className="ri-timer-line"></i>截止时间 {formatDate(post.deadline_at)}
                        </span>
                      )}
                    </div>

                    {/* Actions Row */}
                    <div className="flex items-center justify-between gap-2.5 pt-3.5 border-t border-slate-100 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* 已发布状态：【查看申请列表】为主按钮，【查看岗位详情】为次按钮 */}
                        {statusInfo.type === 'published' && (
                          <>
                            <button
                              type="button"
                              onClick={() => setSelectedJobForApps(post)}
                              className="px-4 py-2 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-xs font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 shadow-md shadow-primary-500/20"
                            >
                              <i className="ri-file-user-line text-sm"></i>
                              查看申请列表
                            </button>
                            <Link
                              to={`/job-postings/${post.id}`}
                              className="px-3.5 py-2 rounded-xl bg-slate-100 text-slate-700 text-xs font-medium hover:bg-slate-200 transition-all cursor-pointer whitespace-nowrap"
                            >
                              查看岗位详情
                            </Link>
                          </>
                        )}

                        {/* 草稿状态：【提交审核】为主按钮，【继续编辑】为次按钮 */}
                        {statusInfo.type === 'draft' && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleSubmitAudit(post.id)}
                              className="px-4 py-2 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-xs font-semibold transition-all cursor-pointer whitespace-nowrap shadow-md shadow-primary-500/20"
                            >
                              提交审核
                            </button>
                            <Link
                              to={`/post-job?id=${post.id}`}
                              className="px-3.5 py-2 rounded-xl bg-slate-100 text-slate-700 text-xs font-medium hover:bg-slate-200 transition-all cursor-pointer whitespace-nowrap"
                            >
                              继续编辑
                            </Link>
                          </>
                        )}

                        {/* 已驳回状态：【修改并重新提交】 */}
                        {statusInfo.type === 'rejected' && (
                          <Link
                            to={`/post-job?id=${post.id}`}
                            className="px-4 py-2 rounded-xl bg-primary-600 hover:bg-primary-700 text-white text-xs font-semibold transition-all cursor-pointer whitespace-nowrap shadow-md shadow-primary-500/20"
                          >
                            修改并重新提交
                          </Link>
                        )}

                        {/* 已关闭状态：不可编辑 */}
                      </div>

                      {/* 更多操作菜单 */}
                      {statusInfo.type === 'published' && (
                        <MoreActionMenu
                          title="更多选项"
                          align="right"
                          buttonClassName="w-8 h-8 rounded-xl border border-slate-200/80 bg-white hover:bg-slate-50 text-slate-500 hover:text-slate-700 flex items-center justify-center transition-all cursor-pointer"
                          items={[
                            {
                              key: 'edit',
                              label: '编辑岗位',
                              icon: 'ri-edit-line',
                              onClick: () => {
                                window.location.href = `/post-job?id=${post.id}`;
                              },
                            },
                            {
                              key: 'close',
                              label: '关闭招聘',
                              icon: 'ri-stop-circle-line',
                              danger: true,
                              onClick: () => setConfirmCloseId(post.id),
                            },
                          ]}
                        />
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}

      {/* 申请列表 Modal 模块 */}
      <JobApplicationsModal
        job={selectedJobForApps}
        onClose={() => setSelectedJobForApps(null)}
        showToast={showToast}
      />

      {/* 确认关闭招聘弹窗 */}
      <ConfirmModal
        open={Boolean(confirmCloseId)}
        title="关闭招聘岗位"
        content="确定要关闭该招聘岗位吗？关闭后该岗位将不再对外展示。"
        type="danger"
        confirmText="确定关闭"
        cancelText="取消"
        loading={isClosing}
        onConfirm={handleConfirmClose}
        onCancel={() => setConfirmCloseId(null)}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </AccountLayout>
  );
}
